import { ContactsList, Segment } from '@dev/ee-api/ee-api-models';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { safelyDecodeBase64Url } from '@dev/ee-tools';

import { ALL_CONTACTS_SEGMENT } from '@/services/constants';
import { getListstStore } from '@/store/lists/actions';
import { getSegmentsStore } from '@/store/segments/actions';

import { BLOCKED_CONTACTS_RULES, STALE_CONTACTS_RULES } from '../../consts';

const isSelectedEntityAList = (entity: ContactsList | Segment): entity is ContactsList =>
    !!(entity as ContactsList)?.ListName;

export const useSelectedEntity = () => {
    const { t } = useTranslation();
    const { selected: storeSelectedList, listNamesCollection } = useSelector(getListstStore);
    const { selected: storeSelectedSegment, collection: segmentsCollection } = useSelector(getSegmentsStore);
    const { listname, segmentname } = useParams<{ listname: string; segmentname: string }>();

    const decodedName = safelyDecodeBase64Url(listname || segmentname);
    const isList = !!listname;

    const selectedSegment = () =>
        storeSelectedSegment ||
        segmentsCollection?.find((single): boolean => {
            if (decodedName === ALL_CONTACTS_SEGMENT) {
                return single.IsAllContacts;
            }
            return single.Name === decodedName;
        });

    const selectedList = () =>
        storeSelectedList || listNamesCollection?.find((single): boolean => single.ListName === decodedName);

    const selectedEntity = useMemo(
        () => (isList ? selectedList() : selectedSegment()),
        [listname, segmentname, storeSelectedList?.ListName, storeSelectedSegment?.Name]
    );

    const isAllContactsSegment = (selectedEntity as Segment)?.IsAllContacts;
    const getSegmentDispllayName = (allContactsName = t('allcontacts')) =>
        isAllContactsSegment ? allContactsName : (selectedEntity as Segment)?.Name;
    const getDisplayName = (allContactsName?: string) =>
        isSelectedEntityAList(selectedEntity) ? selectedEntity?.ListName : getSegmentDispllayName(allContactsName);

    const isBlockedOrStaleSegment =
        !isSelectedEntityAList(selectedEntity) &&
        [...BLOCKED_CONTACTS_RULES, ...STALE_CONTACTS_RULES].includes(selectedEntity?.Rule);

    return {
        selectedEntity,
        isSelectedEntityAList,
        isAllContactsSegment,
        getDisplayName,
        isBlockedOrStaleSegment,
    };
};
